import React from 'react';
import { FileUpload } from 'styled-icons/fa-solid/';
import FilePdfIcon from 'src/assets/images/svg/file-pdf-icon.inline.svg';
import CloudUpload from 'src/assets/images/svg/cloud-upload-icon.inline.svg';
import DeleteIcon from 'src/assets/images/svg/delete-cross-icon.inline.svg';

import { useFileUploader } from 'src/hooks';
import { BaseBox, BaseBoxProps, Flex, Text, FlexCard, Button } from 'src/kit';

export type FileUploaderProps = BaseBoxProps & {
  onChange: (payload: FormData) => void;
  onRemoveFile: ({ index: number }) => void;
  children?: React.ReactNode;
  fileKey?: string;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  text?: string;
  allowedExtensions?: string[];
  fileNames?: null | string[];
  withIcon?: boolean;
  maxFileSize?: string;
};

export const FileUploader = React.forwardRef(function FileUploader(
  {
    children,
    fileKey,
    withIcon,
    onChange,
    onRemoveFile,
    multiple,
    disabled,
    error,
    text,
    allowedExtensions,
    fileNames,
    maxFileSize = '1MB',
    ...styledProps
  }: FileUploaderProps,
  ref
) {
  const { getRootProps, getInputProps } = useFileUploader({ fileKey, multiple, onChange, disabled });

  return (
    <BaseBox ref={ref} {...styledProps}>
      <FlexCard
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={240}
        border="1px dashed"
        borderColor={error ? 'error' : 'paleGreyTwo'}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {withIcon && <BaseBox as={CloudUpload} mb={20} />}
        <Text mb={20} color="textPrimary">
          {text}
        </Text>
        <Text fontSize={12} color="blueGreyTwo">
          <Text as="span" textStyle="caps">
            {allowedExtensions.join(', ')}
          </Text>{' '}
          accepted, maximum file size: {maxFileSize}
        </Text>
      </FlexCard>
      {fileNames?.map((fileName, index) => (
        <Flex key={fileName} mt={20} alignItems="center">
          {fileName.includes('.pdf') ? (
            <BaseBox as={FilePdfIcon} mr={10} />
          ) : (
            <BaseBox as={FileUpload} width={26} height={34} color="icon" mr={10} />
          )}
          <Text mr={10} color="textSecondary">
            {fileName}
          </Text>
          {onRemoveFile && (
            <Button onClick={() => onRemoveFile({ index })} colors="icon">
              <DeleteIcon />
            </Button>
          )}
        </Flex>
      ))}
      {children}
    </BaseBox>
  );
});

FileUploader.defaultProps = {
  fileKey: 'upl_file',
  allowedExtensions: ['pdf', 'doc', 'docx'],
  withIcon: true,
  text: 'Drag and drop file here or click to upload'
};
